// Validation error interface
export interface ValidationError {
  field: string;
  message: string;
  type: 'error' | 'warning' | 'info';
}

// Validation rule interface
export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => string | null;
  email?: boolean;
  phone?: boolean;
  postalCode?: string; // country code
  url?: boolean;
  numeric?: boolean;
  min?: number;
  max?: number;
}

// Validation result interface
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// Country postal code patterns
const POSTAL_CODE_PATTERNS: Record<string, RegExp> = {
  US: /^\d{5}(-\d{4})?$/, // 12345 or 12345-6789
  CA: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, // A1A 1A1
  GB: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i, // SW1A 1AA
  DE: /^\d{5}$/, // 12345
  FR: /^\d{5}$/, // 12345
  AU: /^\d{4}$/, // 1234
  JP: /^\d{3}-\d{4}$/, // 123-4567
  CN: /^\d{6}$/, // 123456
  IN: /^\d{6}$/, // 123456
  BR: /^\d{5}-\d{3}$/, // 12345-678
  MX: /^\d{5}$/, // 12345
  IT: /^\d{5}$/, // 12345
  ES: /^\d{5}$/, // 12345
  NL: /^\d{4} ?[A-Z]{2}$/i, // 1234 AB
  SE: /^\d{3} ?\d{2}$/, // 123 45
  NO: /^\d{4}$/, // 1234
  DK: /^\d{4}$/, // 1234
  FI: /^\d{5}$/, // 12345
  CH: /^\d{4}$/, // 1234
  AT: /^\d{4}$/ // 1234
};

// Common email domain endings accepted in invoice forms.
const ALLOWED_EMAIL_TLDS = [
  '.com',
  '.org',
  '.edu',
  '.gov',
  '.net',
  '.mil',
  '.int',
  '.io',
  '.ai',
  '.app',
  '.dev',
  '.tech',
  '.co',
  '.biz',
  '.info',
  '.me',
  '.xyz',
  '.online',
  '.store',
  '.agency',
  '.digital',
  '.studio',
  '.design',
  '.solutions',
  '.email',
  '.cloud',
  '.uk',
  '.us',
  '.ca',
  '.au',
  '.eu',
  '.ie',
  '.de',
  '.fr',
  '.es',
  '.it',
  '.nl',
  '.be',
  '.pt',
  '.ch',
  '.at',
  '.se',
  '.no',
  '.dk',
  '.fi',
  '.pl',
  '.cz',
  '.gr',
  '.ro',
  '.hu',
  '.bg',
  '.hr',
  '.sk',
  '.si',
  '.ee',
  '.lv',
  '.lt',
  '.in',
  '.sg',
  '.my',
  '.ph',
  '.hk',
  '.jp',
  '.kr',
  '.cn',
  '.tw',
  '.vn',
  '.th',
  '.id',
  '.ae',
  '.sa',
  '.qa',
  '.za',
  '.ng',
  '.ke',
  '.com.au',
  '.net.au',
  '.org.au',
  '.co.uk',
  '.org.uk',
  '.gov.uk',
  '.ac.uk',
  '.co.nz',
  '.co.za',
  '.com.br',
  '.com.mx'
] as const;

// URL validation pattern
const URL_PATTERN = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;

// Phone number patterns by country
const PHONE_PATTERNS: Record<string, RegExp> = {
  US: /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
  CA: /^\+?1?[-.\s]?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,
  GB: /^\+?44?[-.\s]?\(?([0-9]{4,5})\)?[-.\s]?([0-9]{6})$/,
  DE: /^\+?49?[-.\s]?\(?([0-9]{2,5})\)?[-.\s]?([0-9]{3,8})$/,
  FR: /^\+?33?[-.\s]?\(?([0-9]{1})\)?[-.\s]?([0-9]{2})\)?[-.\s]?([0-9]{2})\)?[-.\s]?([0-9]{2})\)?[-.\s]?([0-9]{2})$/,
  AU: /^\+?61?[-.\s]?\(?([0-9]{1})\)?[-.\s]?([0-9]{4})\)?[-.\s]?([0-9]{4})$/,
  JP: /^\+?81?[-.\s]?\(?([0-9]{1,4})\)?[-.\s]?([0-9]{1,4})\)?[-.\s]?([0-9]{4})$/,
  CN: /^\+?86?[-.\s]?([0-9]{3,4})[-.\s]?([0-9]{4})[-.\s]?([0-9]{4})$/,
  IN: /^\+?91?[-.\s]?([0-9]{5})[-.\s]?([0-9]{5})$/,
  BR: /^\+?55?[-.\s]?\(?([0-9]{2})\)?[-.\s]?([0-9]{4,5})[-.\s]?([0-9]{4})$/,
  MX: /^\+?52?[-.\s]?([0-9]{2,3})[-.\s]?([0-9]{4})[-.\s]?([0-9]{4})$/,
  IT: /^\+?39?[-.\s]?([0-9]{2,4})[-.\s]?([0-9]{4,8})$/,
  ES: /^\+?34?[-.\s]?([0-9]{3})[-.\s]?([0-9]{3})[-.\s]?([0-9]{3})$/,
  NL: /^\+?31?[-.\s]?([0-9]{1,2})[-.\s]?([0-9]{8})$/,
  SE: /^\+?46?[-.\s]?([0-9]{1,3})[-.\s]?([0-9]{6,8})$/,
  NO: /^\+?47?[-.\s]?([0-9]{3})[-.\s]?([0-9]{2})[-.\s]?([0-9]{3})$/,
  DK: /^\+?45?[-.\s]?([0-9]{2})[-.\s]?([0-9]{2})[-.\s]?([0-9]{2})$/,
  FI: /^\+?358?[-.\s]?([0-9]{1,2})[-.\s]?([0-9]{6,8})$/,
  CH: /^\+?41?[-.\s]?([0-9]{2,3})[-.\s]?([0-9]{3})[-.\s]?([0-9]{2})$/,
  AT: /^\+?43?[-.\s]?([0-9]{1,4})[-.\s]?([0-9]{3,8})$/
};

// Validation functions
export const validateField = (value: any, rules: ValidationRule, fieldName: string): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Required validation
  if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    errors.push({
      field: fieldName,
      message: `${fieldName} is required`,
      type: 'error'
    });
    return errors; // Stop validation if required field is empty
  }

  // Skip other validations if value is empty and not required
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return errors;
  }

  const stringValue = String(value);

  // Min length validation
  if (rules.minLength && stringValue.length < rules.minLength) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be at least ${rules.minLength} characters`,
      type: 'error'
    });
  }

  // Max length validation
  if (rules.maxLength && stringValue.length > rules.maxLength) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be no more than ${rules.maxLength} characters`,
      type: 'error'
    });
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(stringValue)) {
    errors.push({
      field: fieldName,
      message: `${fieldName} format is invalid`,
      type: 'error'
    });
  }

  // Email validation
  if (rules.email && !isValidEmail(stringValue)) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be a valid email address`,
      type: 'error'
    });
  }

  // URL validation
  if (rules.url && !URL_PATTERN.test(stringValue)) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be a valid URL`,
      type: 'error'
    });
  }

  // Phone validation
  if (rules.phone) {
    // Try to match against common patterns
    const isPhoneValid = Object.values(PHONE_PATTERNS).some(pattern => 
      pattern.test(stringValue.replace(/[\s\-\(\)]/g, ''))
    );
    
    if (!isPhoneValid) {
      errors.push({
        field: fieldName,
        message: `${fieldName} must be a valid phone number`,
        type: 'error'
      });
    }
  }

  // Postal code validation
  if (rules.postalCode && POSTAL_CODE_PATTERNS[rules.postalCode]) {
    const pattern = POSTAL_CODE_PATTERNS[rules.postalCode];
    if (!pattern.test(stringValue)) {
      errors.push({
        field: fieldName,
        message: `${fieldName} must be a valid ${rules.postalCode} postal code`,
        type: 'error'
      });
    }
  }

  // Numeric validation
  if (rules.numeric && isNaN(Number(value))) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be a number`,
      type: 'error'
    });
  }

  // Min value validation
  if (rules.min !== undefined && Number(value) < rules.min) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be at least ${rules.min}`,
      type: 'error'
    });
  }

  // Max value validation
  if (rules.max !== undefined && Number(value) > rules.max) {
    errors.push({
      field: fieldName,
      message: `${fieldName} must be no more than ${rules.max}`,
      type: 'error'
    });
  }

  // Custom validation
  if (rules.custom) {
    const customError = rules.custom(value);
    if (customError) {
      errors.push({
        field: fieldName,
        message: customError,
        type: 'error'
      });
    }
  }

  return errors;
};

// Validate multiple fields
export const validateFields = (data: Record<string, any>, fieldRules: Record<string, ValidationRule>): ValidationResult => {
  const errors: ValidationError[] = [];

  Object.entries(fieldRules).forEach(([fieldName, rules]) => {
    const fieldErrors = validateField(data[fieldName], rules, fieldName);
    errors.push(...fieldErrors);
  });

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Phone number formatting
export const formatPhoneNumber = (phone: string, country: string = 'US'): string => {
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  switch (country) {
    case 'US':
    case 'CA':
      if (cleanPhone.length === 10) {
        return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
      }
      break;
    case 'GB':
      if (cleanPhone.length >= 10) {
        return `${cleanPhone.slice(0, 4)} ${cleanPhone.slice(4)}`;
      }
      break;
    case 'DE':
      if (cleanPhone.length >= 6) {
        return `${cleanPhone.slice(0, 3)} ${cleanPhone.slice(3)}`;
      }
      break;
    case 'FR':
      if (cleanPhone.length >= 8) {
        return `${cleanPhone.slice(0, 2)} ${cleanPhone.slice(2, 4)} ${cleanPhone.slice(4, 6)} ${cleanPhone.slice(6, 8)}`;
      }
      break;
    case 'JP':
      if (cleanPhone.length >= 7) {
        return `${cleanPhone.slice(0, 3)}-${cleanPhone.slice(3)}`;
      }
      break;
    case 'CN':
      if (cleanPhone.length >= 11) {
        return `${cleanPhone.slice(0, 3)} ${cleanPhone.slice(3, 7)} ${cleanPhone.slice(7)}`;
      }
      break;
    case 'BR':
      if (cleanPhone.length >= 8) {
        return `${cleanPhone.slice(0, 5)}-${cleanPhone.slice(5)}`;
      }
      break;
    case 'MX':
      if (cleanPhone.length >= 10) {
        return `${cleanPhone.slice(0, 5)}`;
      }
      break;
  }
  
  return phone; // Return original if no formatting applied
};

// Postal code formatting
export const formatPostalCode = (postalCode: string, country: string = 'US'): string => {
  const cleanPostalCode = postalCode.replace(/[\s\-]/g, '').toUpperCase();
  
  switch (country) {
    case 'US':
      if (cleanPostalCode.length >= 5) {
        return cleanPostalCode.length > 5 
          ? `${cleanPostalCode.slice(0, 5)}-${cleanPostalCode.slice(5)}`
          : cleanPostalCode;
      }
      break;
    case 'CA':
      if (cleanPostalCode.length >= 6) {
        return `${cleanPostalCode.slice(0, 3)} ${cleanPostalCode.slice(3)}`;
      }
      break;
    case 'GB':
      if (cleanPostalCode.length >= 5) {
        return cleanPostalCode.slice(0, -3) + ' ' + cleanPostalCode.slice(-3);
      }
      break;
    case 'NL':
      if (cleanPostalCode.length >= 6) {
        return `${cleanPostalCode.slice(0, 4)} ${cleanPostalCode.slice(4)}`;
      }
      break;
    case 'SE':
      if (cleanPostalCode.length >= 5) {
        return `${cleanPostalCode.slice(0, 3)} ${cleanPostalCode.slice(3)}`;
      }
      break;
  }
  
  return postalCode; // Return original if no formatting applied
};

// Email validation helper
export const isValidEmail = (email: string): boolean => {
  const normalizedEmail = email.trim().toLowerCase();

  if (!normalizedEmail || normalizedEmail.includes(' ')) {
    return false;
  }

  const atIndex = normalizedEmail.indexOf('@');
  if (atIndex <= 0 || atIndex !== normalizedEmail.lastIndexOf('@')) {
    return false;
  }

  const domain = normalizedEmail.slice(atIndex + 1);
  if (domain.length < 4 || domain.startsWith('.') || domain.endsWith('.')) {
    return false;
  }

  if (!domain.includes('.')) {
    return false;
  }

  return ALLOWED_EMAIL_TLDS.some(tld => domain.endsWith(tld));
};

export const isMinimalInvoiceEmailValid = (email: string): boolean => {
  return isValidEmail(email);
};

// URL validation helper
export const isValidUrl = (url: string): boolean => {
  return URL_PATTERN.test(url);
};

// Phone validation helper
export const isValidPhone = (phone: string, country: string = 'US'): boolean => {
  const pattern = PHONE_PATTERNS[country] || PHONE_PATTERNS['US'];
  return pattern.test(phone.replace(/[\s\-\(\)]/g, ''));
};

// Postal code validation helper
export const isValidPostalCode = (postalCode: string, country: string = 'US'): boolean => {
  const pattern = POSTAL_CODE_PATTERNS[country];
  return pattern ? pattern.test(postalCode) : false;
};

// Get validation error for a specific field
export const getFieldError = (errors: ValidationError[], fieldName: string): string | null => {
  const error = errors.find(err => err.field === fieldName);
  return error ? error.message : null;
};

// Check if form is valid
export const isFormValid = (errors: ValidationError[]): boolean => {
  return errors.length === 0;
};

// Get error count by type
export const getErrorCount = (errors: ValidationError[], type: 'error' | 'warning' | 'info'): number => {
  return errors.filter(err => err.type === type).length;
};
